// src/components/usuario/RevisionNivel.jsx
import React, { useMemo, useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import useTemasNivel from "../../hooks/useTemasNivel";
import backgroundImage from "../../assets/img/fondoSiete.png";
import HeaderUsuario from "../header/HeaderUsuario";
import logoCIE from "../../assets/img/logoCIE.svg";

const API_BASE_URL = process.env.REACT_APP_API_URL || "";

const RevisionNivel = () => {
  const { nivelId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const usuario = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("usuario"));
    } catch {
      return null;
    }
  }, []);

  const {
    useTemas: temas,
    loading: loadingTemas,
    obtenerTemas,
  } = useTemasNivel(nivelId, usuario?.id);

  const [idx, setIdx] = useState(0);
  useEffect(() => {
    setIdx(0);
  }, [nivelId]);

  const temaActual = Array.isArray(temas) && temas.length ? temas[idx] : null;

  // --- Contenidos múltiples por tema ---
  const [contenidos, setContenidos] = useState([]); // [{slot, url, tipo}]
  const [selectedSlot, setSelectedSlot] = useState(1);
  const [loadingContenido, setLoadingContenido] = useState(false);
  const [errorContenido, setErrorContenido] = useState("");

  useEffect(() => {
    const cargarContenidosTema = async () => {
      if (!temaActual?.id) {
        setContenidos([]);
        setSelectedSlot(1);
        setErrorContenido("");
        return;
      }
      try {
        setLoadingContenido(true);
        setErrorContenido("");
        const token = localStorage.getItem("token");
        const { data } = await axios.get(
          `${API_BASE_URL}/api/temas/contenido-multimedia/${temaActual.id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        // Normaliza respuesta a array
        const detectarTipo = (url = "") => {
          const clean = (url || "").split("?")[0];
          const ext = (clean.split(".").pop() || "").toLowerCase();
          const IMG = ["jpg", "jpeg", "png", "gif", "webp"];
          const VID = ["mp4", "webm", "mov", "m4v", "avi"];
          if (IMG.includes(ext)) return "image";
          if (VID.includes(ext)) return "video";
          if (ext === "pdf") return "pdf";
          return "url";
        };

        let arr = [];
        if (Array.isArray(data?.contenidos)) {
          arr = data.contenidos;
        } else if (data?.url) {
          arr = [{ slot: 1, url: data.url, tipo: detectarTipo(data.url) }];
        }

        // Guarda sin cache-bust, ordenado por slot
        const normalized = arr
          .map((c) => ({
            slot: Number(c.slot),
            url: c.url,
            tipo: c.tipo || detectarTipo(c.url),
          }))
          .sort((a, b) => a.slot - b.slot);

        setContenidos(normalized);
        setSelectedSlot(normalized[0]?.slot || 1);
      } catch (e) {
        console.error("Error cargando contenidos del tema:", e);
        setContenidos([]);
        setSelectedSlot(1);
        setErrorContenido("No fue posible cargar el contenido multimedia.");
      } finally {
        setLoadingContenido(false);
      }
    };

    cargarContenidosTema();
  }, [temaActual?.id]);

  const irAnterior = () => setIdx((i) => Math.max(0, i - 1));
  const irSiguiente = () => setIdx((i) => Math.min(temas.length - 1, i + 1));

  const cacheBust = (url = "") =>
    url ? `${url}${url.includes("?") ? "&" : "?"}t=${Date.now()}` : url;

  const mimeByExt = {
    mp4: "video/mp4",
    webm: "video/webm",
    mov: "video/quicktime",
    m4v: "video/x-m4v",
    avi: "video/x-msvideo",
  };

  const selectedItem = contenidos.find((c) => c.slot === selectedSlot);

  const renderContenido = (item) => {
    if (!item?.url) {
      return (
        <div className="text-gray-500 bg-gray-50 border rounded p-6 text-center">
          No hay contenido multimedia para este tema.
        </div>
      );
    }

    const bustedUrl = cacheBust(item.url);

    if (item.tipo === "image") {
      return (
        <img
          key={`${selectedSlot}-${bustedUrl}`}
          src={bustedUrl}
          alt="Contenido del tema"
          className="max-w-full max-h-[70vh] object-contain mx-auto rounded"
        />
      );
    }

    if (item.tipo === "video") {
      const clean = item.url.split("?")[0];
      const ext = (clean.split(".").pop() || "").toLowerCase();
      return (
        <video
          key={`${selectedSlot}-${bustedUrl}`}
          src={bustedUrl}
          controls
          className="w-full max-h-[70vh] mx-auto rounded"
        >
          <source src={bustedUrl} type={mimeByExt[ext] || "video/mp4"} />
          Tu navegador no soporta el elemento de video.
        </video>
      );
    }

    if (item.tipo === "pdf") {
      return (
        <div>
          <iframe
            key={`${selectedSlot}-${bustedUrl}`}
            src={bustedUrl}
            title="Documento PDF"
            className="w-full"
            style={{ height: "75vh", border: "none" }}
          />
          <div className="mt-2 text-center">
            <a
              href={bustedUrl}
              target="_blank"
              rel="noreferrer"
              className="underline"
            >
              Abrir PDF en nueva pestaña
            </a>
          </div>
        </div>
      );
    }

    return (
      <div className="text-gray-500 bg-gray-50 border rounded p-6 text-center">
        Tipo de contenido no soportado.
      </div>
    );
  };

  return (
    <div
      className="min-h-screen flex flex-col font-montserrat relative"
      style={{
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      {/* Header usuario */}
      <div className="absolute top-4 right-4 z-10">
        <HeaderUsuario />
      </div>

      {/* Banda blanca con título */}
      <div className="bg-white w-full py-2 text-center text-lg font-extrabold">
        Revisión de contenidos
      </div>

      {/* Contenedor principal */}
      <div className="flex-grow p-4">
        <div className="max-w-6xl mx-auto bg-white rounded-lg p-6 shadow-lg">
          {/* Cabecera nivel + acciones */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-4">
            <div>
              <h1 className="text-2xl font-bold">Nivel {nivelId - 1}</h1>
              <p className="text-xs text-gray-500">
                {loadingTemas
                  ? "Cargando temas…"
                  : `${temas?.length || 0} temas`}
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => navigate(-1)}
                className="px-4 py-2 rounded border hover:bg-gray-50"
              >
                ← Volver
              </button>
              {!loadingTemas && (
                <button
                  onClick={obtenerTemas}
                  className="px-4 py-2 rounded border border-green-700 text-green-700 bg-white hover:bg-green-50"
                  title="Recargar lista de temas"
                >
                  Recargar contenido ↻
                </button>
              )}
            </div>
          </div>

          {/* Selector de tema + navegación */}
          <div className="p-4 border rounded-lg mb-4 bg-gray-50">
            <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
              {/* Línea del tema */}
              <div className="grid grid-cols-[auto,1fr,auto] items-center gap-2 w-full min-w-0">
                <span className="text-sm text-gray-600">Tema:</span>

                <div className="min-w-0">
                  <select
                    className="w-full max-w-full border rounded px-2 py-1 text-sm"
                    disabled={!temas?.length}
                    value={temaActual?.id || ""}
                    onChange={(e) => {
                      const id = Number(e.target.value);
                      const nuevoIndex = temas.findIndex((t) => t.id === id);
                      if (nuevoIndex >= 0) setIdx(nuevoIndex);
                    }}
                  >
                    {temas?.map((t) => (
                      <option key={t.id} value={t.id} title={t.nombre}>
                        {t.nombre}
                      </option>
                    ))}
                  </select>
                </div>

                {temaActual?.completado ? (
                  <span className="text-xs text-green-700">✓ completado</span>
                ) : (
                  <span className="text-xs text-gray-400">• pendiente</span>
                )}
              </div>

              {/* Navegación */}
              <div className="flex items-center gap-2 sm:ml-auto">
                <button
                  onClick={irAnterior}
                  disabled={!temas?.length || idx === 0}
                  className="px-3 py-2 rounded border hover:bg-gray-50 disabled:opacity-50"
                >
                  ⟨ Anterior
                </button>
                <button
                  onClick={irSiguiente}
                  disabled={!temas?.length || idx === temas.length - 1}
                  className="px-3 py-2 rounded border hover:bg-gray-50 disabled:opacity-50"
                >
                  Siguiente ⟩
                </button>
              </div>
            </div>
          </div>

          {/* Visor */}
          {!temas?.length ? (
            <div className="text-gray-500">No hay temas para este nivel.</div>
          ) : (
            <div className="space-y-3">
              <h2 className="text-base sm:text-lg font-bold">
                {temaActual?.nombre}
              </h2>

              <div className="text-xs text-gray-500">
                {loadingContenido ? "Cargando contenido…" : ""}
                {errorContenido && (
                  <span className="text-red-600 ml-2">{errorContenido}</span>
                )}
              </div>

              {/* Tabs/píldoras dinámicas solo para contenidos existentes */}
              {contenidos.length > 1 && (
                <div className="flex justify-center gap-2 mb-2">
                  {contenidos.map((c, i) => (
                    <button
                      key={c.slot}
                      onClick={() => setSelectedSlot(c.slot)}
                      className={`px-3 py-1.5 rounded-full border text-sm transition ${
                        selectedSlot === c.slot
                          ? "bg-green-700 text-white border-green-700"
                          : "bg-white text-green-700 border-green-700 hover:bg-green-50"
                      }`}
                      title={`Ver contenido ${i + 1}`}
                    >
                      {`Contenido ${i + 1}`}
                    </button>
                  ))}
                </div>
              )}

              {/* Render de contenido seleccionado */}
              {contenidos.length === 0 ? (
                <div className="text-gray-500 bg-gray-50 border rounded p-6 text-center">
                  No hay contenido multimedia para este tema.
                </div>
              ) : (
                renderContenido(selectedItem || contenidos[0])
              )}
            </div>
          )}
        </div>
      </div>

      {/* Logo inferior derecha */}
      <div className="absolute bottom-4 right-2 z-10">
        <img src={logoCIE} alt="Logo CIE" className="h-10 w-auto" />
      </div>
    </div>
  );
};

export default RevisionNivel;
